// src/services/authService.ts - Updated with automatic login handling
import { OnboardingData, UserOnboardingInfo } from '@/models/User';
import { apiClient, tokenRefreshClient } from './api';
import { setToken } from '@/utils/tokenStorage';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires: number;
  token_type: string;
}

export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/token', { email, password });
  return response.data;
};

export const refreshToken = async (refreshToken: string): Promise<LoginResponse> => {
  const response = await tokenRefreshClient.post<LoginResponse>('/token/refresh', { refresh_token: refreshToken });
  return response.data;
}

export const requestPasswordReset = async (email: string): Promise<void> => {
  const response = await apiClient.post('/auth/request-password-reset', { email });
  if (response.status !== 200) {
    throw new Error(response.data.message || 'Failed to request password reset');
  }
};

export const setPassword = async (token: string, password: string): Promise<{ access_token: string; refresh_token: string; expires: string; token_type: string }> => {
  const response = await apiClient.post('/auth/set-password', { token, password });
  return response.data;
};

export const getUserOnboardingInfo = async (token: string): Promise<UserOnboardingInfo> => {
  const response = await apiClient.get(`/users/onboarding/${token}`);
  return response.data;
};

export const completeOnboarding = async (onboardingData: OnboardingData): Promise<void> => {
  const response = await apiClient.post('/users/onboarding/complete', onboardingData);
  return response.data;
};

export interface SignupData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  job_title: string;
  organization_name: string;
  selected_plan: string;
  language_preference: string; 
  marketing_emails: boolean;
}

export interface SignupResponse {
  message: string;
  user_id: string;
  organization_id: string;
  access_token: string;
  refresh_token: string;
  expires: number;
  token_type: string;
  checkout_url?: string;
}

/**
 * Sign up a new user and organization with automatic login
 */
export const signUp = async (signupData: SignupData): Promise<SignupResponse> => {
  const response = await apiClient.post<SignupResponse>('/signup', signupData);
  
  // Automatically store the authentication tokens
  if (response.data.access_token && response.data.refresh_token) {
    setToken(response.data.access_token, response.data.refresh_token);
  }
  
  return response.data;
};

/**
 * Reset password with token
 */
export const resetPassword = async (token: string, password: string) => {
  const response = await apiClient.post('/auth/set-password', {
    token,
    password,
  });
  return response.data;
};

/**
 * Login user and store tokens
 */
export const loginAndStoreTokens = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await login(email, password);
  setToken(response.access_token, response.refresh_token);
  return response;
};

/**
 * Exchange payment session ID for authentication tokens
 */
export const exchangePaymentToken = async (sessionId: string): Promise<SignupResponse> => {
  const response = await apiClient.post<SignupResponse>('/exchange-payment-token', {
    session_id: sessionId
  });
  
  // Automatically store the authentication tokens
  if (response.data.access_token && response.data.refresh_token) {
    setToken(response.data.access_token, response.data.refresh_token);
  }
  
  return response.data;
};


/**
 * Verify email with token (NOT USED)
 */
export const verifyEmail = async (token: string): Promise<void> => {
  const response = await apiClient.post('/auth/verify-email', { token });
  return response.data;
};

/**
 * Resend email verification  (NOT USED)
 */
export const resendEmailVerification = async (): Promise<void> => {
  const response = await apiClient.post('/auth/resend-verification');
  return response.data;
};